import { MAX_SAMPLE_BYTES } from '../domain/schemas';
import { Observable } from '../lib/store';

/**
 * Microphone recording with MediaRecorder.
 *
 * Privacy and resource rules:
 *  - the microphone is only requested from `start()`, which the UI calls from a click;
 *    nothing records automatically or in the background;
 *  - every exit path (stop, limit reached, error, discard, dispose) stops all
 *    MediaStream tracks so the browser's "recording" indicator goes away;
 *  - the recording stays in memory until the user saves it to the project (IndexedDB)
 *    or discards it; nothing is uploaded anywhere.
 */
export const MAX_RECORDING_SECONDS = 120;

/** Preferred containers, best first. Safari only offers MP4/AAC. */
export const RECORDING_TYPES = ['audio/webm;codecs=opus', 'audio/ogg;codecs=opus', 'audio/webm', 'audio/mp4'];

export type RecorderStatus = 'idle' | 'requesting' | 'recording' | 'recorded' | 'error';

export interface RecorderState {
  status: RecorderStatus;
  elapsedSec: number;
  sizeBytes: number;
  mimeType: string | null;
  /** Why recording stopped on its own (time or size limit). */
  notice?: string;
  error?: string;
  errorCode?: 'unsupported' | 'denied' | 'no-device' | 'busy' | 'failed';
}

export interface Recording {
  blob: Blob;
  /** Container type without codec parameters, e.g. "audio/webm". */
  mimeType: string;
  durationSec: number;
  /** Object URL for previewing the take; revoked when the take is discarded or saved. */
  url: string | null;
}

type MediaRecorderCtor = {
  new (stream: MediaStream, options?: MediaRecorderOptions): MediaRecorder;
  isTypeSupported?(type: string): boolean;
};

export interface RecorderDeps {
  mediaDevices?: Pick<MediaDevices, 'getUserMedia'>;
  MediaRecorder?: MediaRecorderCtor;
  now?: () => number;
  maxSeconds?: number;
  maxBytes?: number;
}

const initial: RecorderState = { status: 'idle', elapsedSec: 0, sizeBytes: 0, mimeType: null };

export function pickRecordingType(ctor: MediaRecorderCtor): string | null {
  if (typeof ctor.isTypeSupported !== 'function') return null;
  return RECORDING_TYPES.find((t) => ctor.isTypeSupported!(t)) ?? null;
}

function describeGetUserMediaError(error: unknown): Pick<RecorderState, 'error' | 'errorCode'> {
  const name = (error as { name?: string } | null)?.name;
  switch (name) {
    case 'NotAllowedError':
    case 'SecurityError':
      return {
        errorCode: 'denied',
        error: 'Microphone access was blocked. Allow it for this site in the browser’s address bar or settings, then try again.',
      };
    case 'NotFoundError':
    case 'OverconstrainedError':
      return { errorCode: 'no-device', error: 'No microphone was found. Connect one and try again.' };
    case 'NotReadableError':
    case 'AbortError':
      return { errorCode: 'busy', error: 'The microphone is in use by another application or could not be opened.' };
    default:
      return { errorCode: 'failed', error: 'The microphone could not be started.' };
  }
}

export class MicRecorder {
  readonly state = new Observable<RecorderState>(initial);
  private readonly deps: Required<Pick<RecorderDeps, 'now' | 'maxSeconds' | 'maxBytes'>> & RecorderDeps;
  private stream: MediaStream | null = null;
  private recorder: MediaRecorder | null = null;
  private chunks: Blob[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private startedAt = 0;
  private recording: Recording | null = null;
  private attempt = 0;
  private stopped: Promise<void> | null = null;

  constructor(deps: RecorderDeps = {}) {
    this.deps = {
      mediaDevices: deps.mediaDevices ?? (typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined),
      MediaRecorder: deps.MediaRecorder ?? (typeof MediaRecorder !== 'undefined' ? MediaRecorder : undefined),
      now: deps.now ?? (() => performance.now()),
      maxSeconds: deps.maxSeconds ?? MAX_RECORDING_SECONDS,
      maxBytes: deps.maxBytes ?? MAX_SAMPLE_BYTES,
    };
  }

  get snapshot(): RecorderState {
    return this.state.get();
  }

  /** True when this browser can record at all. */
  get supported(): boolean {
    return !!this.deps.mediaDevices?.getUserMedia && !!this.deps.MediaRecorder;
  }

  /** Whether the microphone stream is currently open (for tests and the debug panel). */
  get streamOpen(): boolean {
    return this.stream !== null;
  }

  /** Asks for the microphone and starts recording. Must be called from a user action. */
  async start(): Promise<void> {
    const status = this.snapshot.status;
    if (status === 'requesting' || status === 'recording') return;
    // Recording again discards the take waiting for review: release its preview URL.
    if (this.recording?.url) URL.revokeObjectURL(this.recording.url);
    this.recording = null;
    const { mediaDevices, MediaRecorder: Recorder } = this.deps;
    if (!mediaDevices?.getUserMedia || !Recorder) {
      this.state.set({
        ...initial,
        status: 'error',
        errorCode: 'unsupported',
        error: 'This browser cannot record audio (MediaRecorder or microphone access is unavailable). Try a current Chrome, Edge or Firefox.',
      });
      return;
    }
    const attempt = ++this.attempt;
    this.state.set({ ...initial, status: 'requesting' });
    let stream: MediaStream;
    try {
      stream = await mediaDevices.getUserMedia({
        audio: { echoCancellation: false, noiseSuppression: false, autoGainControl: false },
        video: false,
      });
    } catch (error) {
      if (attempt === this.attempt) this.state.set({ ...initial, status: 'error', ...describeGetUserMediaError(error) });
      return;
    }
    if (attempt !== this.attempt) {
      // Cancelled (discard/dispose) while the permission prompt was open.
      stream.getTracks().forEach((t) => t.stop());
      return;
    }
    this.stream = stream;
    const type = pickRecordingType(Recorder);
    let recorder: MediaRecorder;
    try {
      recorder = new Recorder(stream, type ? { mimeType: type } : undefined);
    } catch {
      this.releaseStream();
      this.state.set({ ...initial, status: 'error', errorCode: 'unsupported', error: 'This browser cannot record in a supported audio format.' });
      return;
    }
    this.recorder = recorder;
    this.chunks = [];
    let size = 0;
    recorder.ondataavailable = (event: BlobEvent) => {
      if (!event.data || event.data.size === 0) return;
      this.chunks.push(event.data);
      size += event.data.size;
      this.state.update({ sizeBytes: size });
      if (size >= this.deps.maxBytes * 0.95 && recorder.state === 'recording') {
        void this.stop(`Stopped at the ${Math.round(this.deps.maxBytes / 1048576)} MB size limit.`);
      }
    };
    recorder.onerror = () => {
      this.cleanup();
      this.state.set({ ...initial, status: 'error', errorCode: 'failed', error: 'Recording failed unexpectedly. Nothing was saved.' });
    };
    this.startedAt = this.deps.now();
    try {
      recorder.start(250);
    } catch {
      this.cleanup();
      this.state.set({ ...initial, status: 'error', errorCode: 'failed', error: 'Recording could not start.' });
      return;
    }
    this.state.set({ ...initial, status: 'recording', mimeType: recorder.mimeType || type });
    this.timer = setInterval(() => {
      const elapsedSec = (this.deps.now() - this.startedAt) / 1000;
      this.state.update({ elapsedSec });
      if (elapsedSec >= this.deps.maxSeconds) void this.stop(`Stopped at the ${this.deps.maxSeconds} s length limit.`);
    }, 100);
  }

  /** Stops recording and keeps the take for preview. Releases the microphone. */
  stop(notice?: string): Promise<void> {
    const recorder = this.recorder;
    if (!recorder || this.snapshot.status !== 'recording') return this.stopped ?? Promise.resolve();
    this.stopped ??= new Promise<void>((resolve) => {
      const durationSec = Math.min(this.deps.maxSeconds, (this.deps.now() - this.startedAt) / 1000);
      const finish = () => {
        const mimeType = (recorder.mimeType || this.snapshot.mimeType || this.chunks[0]?.type || 'audio/webm').split(';')[0]!.trim();
        const blob = new Blob(this.chunks, { type: mimeType });
        this.cleanup();
        this.stopped = null;
        if (blob.size === 0) {
          this.state.set({ ...initial, status: 'error', errorCode: 'failed', error: 'The recording is empty. Check the microphone and try again.' });
        } else if (blob.size > this.deps.maxBytes) {
          this.state.set({ ...initial, status: 'error', errorCode: 'failed', error: 'The recording is larger than the size limit and was discarded.' });
        } else {
          const url = typeof URL.createObjectURL === 'function' ? URL.createObjectURL(blob) : null;
          this.recording = { blob, mimeType, durationSec, url };
          this.state.set({ ...initial, status: 'recorded', elapsedSec: durationSec, sizeBytes: blob.size, mimeType, notice });
        }
        resolve();
      };
      recorder.onstop = finish;
      try {
        recorder.stop();
      } catch {
        finish();
      }
    });
    // Release the microphone right away, even before the last data arrives.
    this.stopTimer();
    this.releaseStream();
    return this.stopped;
  }

  /** The finished take (after `stop`), or null. */
  get take(): Recording | null {
    return this.recording;
  }

  /** Throws the take away (or cancels a pending permission request). */
  discard(): void {
    this.attempt++;
    if (this.recorder && this.recorder.state !== 'inactive') {
      this.recorder.onstop = null;
      try {
        this.recorder.stop();
      } catch {
        // Already inactive.
      }
    }
    this.cleanup();
    this.stopped = null;
    if (this.recording?.url) URL.revokeObjectURL(this.recording.url);
    this.recording = null;
    this.state.set(initial);
  }

  /** Called when the recording UI unmounts: nothing may keep the microphone open. */
  dispose(): void {
    this.discard();
  }

  private stopTimer(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }

  private releaseStream(): void {
    this.stream?.getTracks().forEach((t) => t.stop());
    this.stream = null;
  }

  private cleanup(): void {
    this.stopTimer();
    this.releaseStream();
    if (this.recorder) {
      this.recorder.ondataavailable = null;
      this.recorder.onerror = null;
    }
    this.recorder = null;
    this.chunks = [];
  }
}
